import { useState, useEffect, useMemo, useCallback } from 'react';
import { Rocket, Plus, Search, X, Settings, Layers } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { apiClient } from '../lib/api-client';
import { ProductCard } from '../components/cicd/ProductCard';
import { ProductFormDialog } from '../components/cicd/ProductFormDialog';
import { TenantAssociationDialog } from '../components/cicd/TenantAssociationDialog';
import { ProductWorkflowHistory } from '../components/cicd/ProductWorkflowHistory';
import { WebhookEventsTab } from '../components/cicd/WebhookEventsTab';
import { Skeleton } from '../components/ui/Skeleton';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { PillTabs, PillTabsList, PillTabsTrigger, PillTabsContent } from '../components/ui/PillTabs';
import { ToastContainer } from '../components/ui/Toast';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Input } from '../components/ui/Input';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../contexts/AuthContext';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';

type CicdProduct = {
  id: string;
  tenantAssociations?: Array<{ tenantCode: string; environments?: Record<string, unknown> }>;
  [key: string]: unknown;
};

type ProductsCatalog = {
  commonProducts: CicdProduct[];
  tenantSections: Array<{ tenantCode: string; products: CicdProduct[] }>;
};

function matchesSearch(product: CicdProduct, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const repos = (product.repos as Array<{ name?: string; repoUrl?: string }>) || [];
  return (
    product.id.toLowerCase().includes(q) ||
    String(product.workspaceDir || '').toLowerCase().includes(q) ||
    repos.some((r) => r.name?.toLowerCase().includes(q) || r.repoUrl?.toLowerCase().includes(q))
  );
}

function productMatchesTenants(product: CicdProduct, tenantCodes: string[]): boolean {
  if (tenantCodes.length === 0) return true;
  const allowed = new Set(tenantCodes.map((t) => t.toLowerCase()));
  const associations = product.tenantAssociations || [];
  return associations.some((a) => allowed.has(String(a.tenantCode).toLowerCase()));
}

function findProductInCatalog(catalog: ProductsCatalog, productId: string): CicdProduct | null {
  const common = catalog.commonProducts.find((p) => p.id === productId);
  if (common) return common;
  for (const section of catalog.tenantSections) {
    const match = section.products.find((p) => p.id === productId);
    if (match) return match;
  }
  return null;
}

function ProductGrid({
  products,
  onEdit,
  onDelete,
  onManageTenants,
  onEnableWebhook,
  onDisableWebhook,
  onViewHistory,
  canManage,
  canManageWebhooks,
  canViewHistory,
}: {
  products: CicdProduct[];
  onEdit: (product: CicdProduct) => void;
  onDelete: (productId: string) => void;
  onManageTenants: (product: CicdProduct) => void;
  onEnableWebhook: (productId: string) => void;
  onDisableWebhook: (productId: string) => void;
  onViewHistory: (product: CicdProduct) => void;
  canManage: boolean;
  canManageWebhooks: boolean;
  canViewHistory: boolean;
}) {
  if (products.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onEdit={onEdit}
          onDelete={onDelete}
          onManageTenants={onManageTenants}
          onEnableWebhook={onEnableWebhook}
          onDisableWebhook={onDisableWebhook}
          onViewHistory={onViewHistory}
          canManage={canManage}
          canManageWebhooks={canManageWebhooks}
          canViewHistory={canViewHistory}
        />
      ))}
    </div>
  );
}

export function CICD() {
  const [activeTab, setActiveTab] = useState<'products' | 'events'>('products');
  const [catalog, setCatalog] = useState<ProductsCatalog>({ commonProducts: [], tenantSections: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: 'success' | 'error' | 'warning' | 'info' }>>([]);

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<CicdProduct | null>(null);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [tenantDialogProduct, setTenantDialogProduct] = useState<CicdProduct | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState<CicdProduct | null>(null);

  const { isAllowed: canManage } = usePermission('cicd', 'manage_products');
  const { isAllowed: canManageWebhooks } = usePermission('cicd', 'manage_webhooks');
  const { isAllowed: canViewHistory } = usePermission('cicd', 'view_history');
  const { accessibleTenants, selectedTenants } = useAuth();
  const tenantMetaMap = useTenantMetaMap(true);

  const tenantsToQuery = useMemo(
    () => (selectedTenants.length > 0 ? selectedTenants : accessibleTenants).map((t) => String(t).toLowerCase()),
    [accessibleTenants, selectedTenants],
  );

  const tenantsQueryKey = tenantsToQuery.join(',');

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  };

  const loadProducts = useCallback(async () => {
    if (tenantsToQuery.length === 0) {
      setCatalog({ commonProducts: [], tenantSections: [] });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await apiClient.getCicdProductsCatalog(tenantsToQuery);
      setCatalog({
        commonProducts: data.commonProducts || [],
        tenantSections: data.tenantSections || [],
      });
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  }, [tenantsQueryKey]);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  const refreshDialogProduct = async (productId: string) => {
    const data = await apiClient.getCicdProductsCatalog(tenantsToQuery);
    const nextCatalog = {
      commonProducts: data.commonProducts || [],
      tenantSections: data.tenantSections || [],
    };
    setCatalog(nextCatalog);
    const updated = findProductInCatalog(nextCatalog, productId);
    if (updated) setTenantDialogProduct(updated);
  };

  const handleCreateProduct = async (product: CicdProduct) => {
    await apiClient.createCicdProduct(product);
    addToast(`Product "${product.id}" created`, 'success');
    await loadProducts();
  };

  const handleEditProduct = async (product: CicdProduct) => {
    await apiClient.updateCicdProduct(product.id, product);
    addToast(`Product "${product.id}" updated`, 'success');
    await loadProducts();
  };

  const handleDeleteProduct = async (productId: string) => {
    await apiClient.deleteCicdProduct(productId);
    addToast(`Product "${productId}" deleted`, 'success');
    await loadProducts();
  };

  const handleEnableWebhook = async (productId: string) => {
    try {
      await apiClient.enableCicdProductWebhook(productId);
      addToast(`Webhooks enabled for "${productId}"`, 'success');
      await loadProducts();
    } catch (err: any) {
      addToast(`Failed to enable webhooks: ${err.message}`, 'error');
    }
  };

  const handleDisableWebhook = async (productId: string) => {
    try {
      await apiClient.disableCicdProductWebhook(productId);
      addToast(`Webhooks disabled for "${productId}"`, 'success');
      await loadProducts();
    } catch (err: any) {
      addToast(`Failed to disable webhooks: ${err.message}`, 'error');
    }
  };

  const handleAssociateTenant = async (tenantCode: string) => {
    if (!tenantDialogProduct) return;
    await apiClient.associateCicdProductTenant(tenantDialogProduct.id, tenantCode);
    addToast(`Tenant "${tenantCode}" associated`, 'success');
    await refreshDialogProduct(tenantDialogProduct.id);
  };

  const handleDisassociateTenant = async (tenantCode: string) => {
    if (!tenantDialogProduct) return;
    await apiClient.disassociateCicdProductTenant(tenantDialogProduct.id, tenantCode);
    addToast(`Tenant "${tenantCode}" disassociated`, 'success');
    await refreshDialogProduct(tenantDialogProduct.id);
  };

  const handleSetEnvConfig = async (tenantCode: string, env: string, config: unknown) => {
    if (!tenantDialogProduct) return;
    await apiClient.setCicdProductEnvConfig(tenantDialogProduct.id, tenantCode, env, config);
    addToast(`Environment "${env}" configured for ${tenantCode}`, 'success');
    await refreshDialogProduct(tenantDialogProduct.id);
  };

  const handleRemoveEnvConfig = async (tenantCode: string, env: string) => {
    if (!tenantDialogProduct) return;
    await apiClient.removeCicdProductEnvConfig(tenantDialogProduct.id, tenantCode, env);
    addToast(`Environment "${env}" removed from ${tenantCode}`, 'success');
    await refreshDialogProduct(tenantDialogProduct.id);
  };

  const filteredCommonProducts = useMemo(() => {
    return catalog.commonProducts.filter((product) => {
      const hasAssociations = (product.tenantAssociations?.length ?? 0) > 0;
      const visibleForTenants = productMatchesTenants(product, tenantsToQuery);
      if (!visibleForTenants && hasAssociations) return false;
      if (!visibleForTenants && !hasAssociations && !canManage) return false;
      return matchesSearch(product, searchQuery);
    });
  }, [catalog.commonProducts, tenantsToQuery, canManage, searchQuery]);

  const filteredTenantSections = useMemo(() => {
    return catalog.tenantSections
      .map((section) => ({
        ...section,
        products: section.products.filter((product) => matchesSearch(product, searchQuery)),
      }))
      .filter((section) => section.products.length > 0);
  }, [catalog.tenantSections, searchQuery]);

  const totalProductCount = useMemo(() => {
    const tenantIds = new Set<string>();
    filteredTenantSections.forEach((section) => section.products.forEach((p) => tenantIds.add(p.id)));
    return filteredCommonProducts.length + tenantIds.size;
  }, [filteredCommonProducts, filteredTenantSections]);

  const hasVisibleProducts = filteredCommonProducts.length > 0 || filteredTenantSections.length > 0;

  const cardHandlers = {
    onEdit: (p: CicdProduct) => {
      setEditingProduct(p);
      setFormDialogOpen(true);
    },
    onDelete: handleDeleteProduct,
    onManageTenants: (p: CicdProduct) => {
      setTenantDialogProduct(p);
      setTenantDialogOpen(true);
    },
    onEnableWebhook: handleEnableWebhook,
    onDisableWebhook: handleDisableWebhook,
    onViewHistory: (p: CicdProduct) => {
      setHistoryProduct(p);
      setHistoryDialogOpen(true);
    },
    canManage,
    canManageWebhooks,
    canViewHistory,
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Rocket}
        iconColor="text-orange-600 dark:text-orange-400"
        iconBgColor="bg-orange-100 dark:bg-orange-900/30"
        description="CI/CD pipeline management — products, webhooks, and PR environments"
        count={totalProductCount}
        countLabel="products"
        countColor="text-orange-600 dark:text-orange-400"
        actions={
          (() => {
            const actionItems = [
              ...(canManage
                ? [{
                    label: 'Add Product',
                    icon: <Plus className="w-4 h-4" />,
                    onClick: () => {
                      setEditingProduct(null);
                      setFormDialogOpen(true);
                    },
                  }]
                : []),
            ].filter(Boolean) as Array<{ label: string; icon: JSX.Element; onClick: () => void }>;

            if (actionItems.length === 0) return undefined;

            return (
              <div className="w-full sm:w-auto sm:flex sm:self-stretch">
                <DropdownMenu
                  className="w-full h-full"
                  trigger={
                    <Button variant="outline" size="sm" className="w-full h-full text-sm py-2">
                      <Settings className="w-4 h-4 mr-2" />
                      Actions
                    </Button>
                  }
                  items={actionItems}
                />
              </div>
            );
          })()
        }
      />

      <PillTabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'products' | 'events')}>
        <PillTabsList>
          <PillTabsTrigger value="products">
            Products
            <Badge variant="outline" className="ml-2">{totalProductCount}</Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="events">
            Webhook Events
          </PillTabsTrigger>
        </PillTabsList>

        <PillTabsContent value="products" className="mt-4">
          <div className="mb-4">
            <div className="relative max-w-2xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products by name, workspace, or repository..."
                className="pl-9 pr-10"
              />
              {searchQuery.trim() && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-500 dark:text-red-400">{error}</div>
          ) : tenantsToQuery.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No tenants available for your account.
            </div>
          ) : !hasVisibleProducts ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No products match your search' : 'No products configured for the selected tenants'}
            </div>
          ) : (
            <div className="space-y-8">
              {filteredCommonProducts.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Layers className="w-5 h-5 text-orange-600 dark:text-orange-400 shrink-0" />
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                        Common Products
                      </h2>
                      <Badge variant="outline" className="text-xs shrink-0">master.yaml</Badge>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                      {filteredCommonProducts.length} product{filteredCommonProducts.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {filteredCommonProducts.length > 0 ? (
                    <ProductGrid products={filteredCommonProducts} {...cardHandlers} />
                  ) : null}
                </section>
              )}

              {filteredTenantSections.map((section) => {
                const tenantMeta = resolveTenantMeta(tenantMetaMap, section.tenantCode);
                return (
                <section key={section.tenantCode} className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <TenantHeading
                      tenant={tenantMeta}
                      title={tenantMeta.name}
                      subtitle={tenantMeta.code}
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                      {section.products.length} product{section.products.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <ProductGrid products={section.products} {...cardHandlers} />
                </section>
                );
              })}
            </div>
          )}
        </PillTabsContent>

        <PillTabsContent value="events" className="mt-4">
          <WebhookEventsTab />
        </PillTabsContent>
      </PillTabs>

      <ProductFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        product={editingProduct}
        onSave={editingProduct ? handleEditProduct : handleCreateProduct}
      />

      {tenantDialogProduct && (
        <TenantAssociationDialog
          open={tenantDialogOpen}
          onOpenChange={setTenantDialogOpen}
          product={tenantDialogProduct}
          accessibleTenants={accessibleTenants}
          onAssociate={handleAssociateTenant}
          onDisassociate={handleDisassociateTenant}
          onSetEnvConfig={handleSetEnvConfig}
          onRemoveEnvConfig={handleRemoveEnvConfig}
        />
      )}

      {historyProduct && (
        <ProductWorkflowHistory
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          product={historyProduct}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
    </div>
  );
}
